import { useFlowSessionById, useUpdateFlowSession } from "@/api/flow_sessions";
import { CommandPermissionsHelpDialog } from "@/components/ToolCall/ToolConfirmation";
import { cn } from "@/utils";
import { CircleHelp, ShieldAlert, ShieldCheck } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

interface SessionBypassNoticeProps {
  /** The flow session this composer is attached to. */
  flowSessionId?: string;
  className?: string;
}

/**
 * Sits directly above the composer while the session is auto-accepting every
 * command (tool_user_confirmations_disabled). It states the bypass plainly and
 * offers a one-click return to confirmation mode. Renders nothing when
 * confirmations are on, so it can be mounted unconditionally.
 *
 * Uses the design system's `lc-warning` token (the same caution treatment as
 * AlertBanner's "warning" variant), sized down to a single composer-width row.
 */
export function SessionBypassNotice({
  flowSessionId,
  className,
}: SessionBypassNoticeProps) {
  // Shares the query key used elsewhere, so React Query dedupes — no extra fetch.
  const { data: flowSession } = useFlowSessionById(flowSessionId || "");
  const confirmationsBypassed = !!flowSession?.tool_user_confirmations_disabled;

  const [helpOpen, setHelpOpen] = useState(false);

  const { mutate: updateFlowSession, isPending: isRestoring } =
    useUpdateFlowSession({
      onError: (error) => {
        toast.error(error.message);
      },
    });

  if (!flowSessionId || !confirmationsBypassed) return null;

  const restoreConfirmations = () =>
    updateFlowSession({
      flowSessionId,
      data: { tool_user_confirmations_disabled: false },
    });

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-xl border border-lc-warning/10 bg-lc-warning/10 px-2.5 py-1.5 text-xs",
        className,
      )}
    >
      <ShieldAlert className="size-3.5 shrink-0 text-lc-warning" />
      <span
        className="min-w-0 flex-1 truncate text-lc-warning"
        title="Auto-accepting all commands this session"
      >
        Auto-accepting all commands this session
      </span>
      <button
        type="button"
        onClick={restoreConfirmations}
        disabled={isRestoring}
        className={cn(
          "inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 font-medium",
          "text-lc-warning transition-colors hover:bg-lc-warning/15",
          "disabled:pointer-events-none disabled:opacity-50",
        )}
      >
        <ShieldCheck className="size-3.5 shrink-0" />
        Restore confirmations
      </button>
      <button
        type="button"
        onClick={() => setHelpOpen(true)}
        aria-label="How command confirmations work"
        className={cn(
          "inline-flex size-5 shrink-0 items-center justify-center rounded-md",
          "text-lc-warning/70 transition-colors hover:bg-lc-warning/15 hover:text-lc-warning",
        )}
      >
        <CircleHelp className="size-3.5" />
      </button>
      <CommandPermissionsHelpDialog
        open={helpOpen}
        onOpenChange={setHelpOpen}
      />
    </div>
  );
}
